import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Search, Loader2, Gavel, ScrollText, HelpCircle, FileText,
  ClipboardList, Trash2, UserCog, Building2, User, Wallet, Eye,
  Filter, X, Briefcase, TrendingUp, CheckCircle2, Clock, Flag, RotateCcw,
  PauseCircle, Archive,
} from "lucide-react";

const TIPOS = [
  { value: "judicial", label: "Judicial", Icon: Gavel },
  { value: "notarial", label: "Notarial", Icon: ScrollText },
  { value: "consulta", label: "Consulta", Icon: HelpCircle },
  { value: "tramite", label: "Trámite", Icon: FileText },
  { value: "otro", label: "Otro", Icon: ClipboardList },
];
const ESTADOS = [
  { value: "en_progreso", label: "En progreso", dot: "#D97706", cls: "bg-[#FEE9CC] text-[#8A4A00] border-[#F3B872]", icon: Clock },
  { value: "activo",      label: "Activo",      dot: "#4A6D8C", cls: "bg-[#DDE9F3] text-[#2C4A63] border-[#8FB0CB]", icon: TrendingUp },
  { value: "pausado",     label: "Pausado",     dot: "#B88537", cls: "bg-[#FBEED4] text-[#7A5312] border-[#E5C892]", icon: PauseCircle },
  { value: "cerrado",     label: "Finalizado",  dot: "#16A34A", cls: "bg-[#D8EFDD] text-[#155724] border-[#7BC48A]", icon: CheckCircle2 },
  { value: "archivado",   label: "Archivado",   dot: "#6B7280", cls: "bg-[#F3F4F6] text-[#374151] border-[#9CA3AF]", icon: Archive },
];
const COBRO_ESTADOS = [
  { value: "pendiente", label: "Pendiente" },
  { value: "parcial", label: "Parcial" },
  { value: "pagado", label: "Pagado" },
];
const METODOS_PAGO = ["Transferencia", "Sinpe Móvil", "Efectivo", "Cheque", "Tarjeta", "Otro"];
const MONEDAS = ["CRC", "USD"];

const EMPTY_PROC = {
  cliente_id: "", titulo: "", tipo: "judicial", descripcion: "",
  fecha_inicio: "", fecha_cierre: "", estado: "en_progreso",
  abogado_asignado: "", numero_expediente: "", notas: "",
};
const EMPTY_COBRO = {
  concepto: "", monto: "", moneda: "CRC", fecha: "",
  estado: "pendiente", metodo_pago: "", factura: "", notas: "",
};

const inputCls = "h-11 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]";
const labelCls = "text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold";

function fmtCRC(n) {
  return "₡ " + new Intl.NumberFormat("es-CR").format(Math.round(Number(n) || 0));
}
function tipoBadge(tipo) {
  const t = TIPOS.find((x) => x.value === tipo);
  const Icon = t?.Icon || ClipboardList;
  return (
    <Badge variant="outline" className="border-[#E5DFD3] bg-[#F5EEE0] text-[#3d2a2a] font-normal">
      <Icon className="h-3 w-3 mr-1" strokeWidth={1.8} />{t?.label || tipo}
    </Badge>
  );
}
function estadoBadge(estado) {
  const e = ESTADOS.find((x) => x.value === estado);
  const Icon = e?.icon;
  return (
    <Badge className={`${e?.cls || "bg-white"} font-semibold border inline-flex items-center gap-1`}>
      {Icon && <Icon className="h-3 w-3" strokeWidth={2.2} />}
      {e?.label || estado}
    </Badge>
  );
}

// ============================================================================
export default function Procesos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = ["super_admin", "admin", "abogado"].includes(user?.role);
  const canDelete = ["super_admin", "admin", "abogado"].includes(user?.role);
  const isAbogadoPrincipal = !!user?.is_abogado_principal;
  const [finalizeTarget, setFinalizeTarget] = useState(null);

  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [abogados, setAbogados] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [query, setQuery] = useState("");
  const [fEstado, setFEstado] = useState("todos");
  const [fTipo, setFTipo] = useState("todos");
  const [fAbogado, setFAbogado] = useState("todos");
  const [fCliente, setFCliente] = useState("todos");

  // Dialogs
  const [procDialog, setProcDialog] = useState({ open: false, editing: null });
  const [cobroDialog, setCobroDialog] = useState({ open: false, proceso: null });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/procesos")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Error cargando procesos"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get("/clientes").then((r) => setClientes(r.data)).catch(() => {});
    api.get("/abogados").then((r) => setAbogados(r.data.filter((a) => a.activo))).catch(() => {});
  }, []);

  const abogadosOptions = useMemo(() => {
    // Combine abogados from /abogados + those already assigned to procesos (in case they don't exist as records)
    const set = new Set(abogados.map((a) => a.nombre));
    items.forEach((p) => { if (p.abogado_asignado) set.add(p.abogado_asignado); });
    return Array.from(set).filter(Boolean).sort();
  }, [abogados, items]);

  const filtered = useMemo(() => {
    return items.filter((p) => {
      if (fEstado !== "todos" && p.estado !== fEstado) return false;
      if (fTipo !== "todos" && p.tipo !== fTipo) return false;
      if (fAbogado !== "todos" && p.abogado_asignado !== fAbogado) return false;
      if (fCliente !== "todos" && p.cliente_id !== fCliente) return false;
      const q = query.toLowerCase().trim();
      if (!q) return true;
      return [
        p.titulo, p.numero_expediente, p.abogado_asignado,
        p.cliente_nombre, p.cliente_identificacion, p.descripcion,
      ].filter(Boolean).some((v) => v.toLowerCase().includes(q));
    });
  }, [items, query, fEstado, fTipo, fAbogado, fCliente]);

  const stats = useMemo(() => ({
    total: filtered.length,
    en_progreso: filtered.filter((p) => p.estado === "en_progreso" || p.estado === "activo").length,
    cerrados: filtered.filter((p) => p.estado === "cerrado" || p.estado === "archivado").length,
    facturado: filtered.reduce((sum, p) => sum + (p.total_facturado || 0), 0),
    cobrado: filtered.reduce((sum, p) => sum + (p.monto_cobrado || 0), 0),
  }), [filtered]);

  const anyFilterActive = query || fEstado !== "todos" || fTipo !== "todos" || fAbogado !== "todos" || fCliente !== "todos";
  const clearFilters = () => {
    setQuery(""); setFEstado("todos"); setFTipo("todos"); setFAbogado("todos"); setFCliente("todos");
  };

  const del = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/procesos/${deleteTarget.id}`);
      toast.success("Proceso eliminado");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al eliminar");
    }
  };

  const finalizar = async () => {
    if (!finalizeTarget) return;
    try {
      await api.post(`/procesos/${finalizeTarget.id}/finalizar`);
      toast.success("Proceso finalizado");
      setFinalizeTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo finalizar");
    }
  };

  const reabrir = async (p) => {
    if (!window.confirm(`¿Reabrir el proceso "${p.titulo}"?`)) return;
    try {
      await api.post(`/procesos/${p.id}/reabrir`);
      toast.success("Proceso reabierto");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo reabrir");
    }
  };

  return (
    <div className="space-y-8" data-testid="module-procesos">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#C25A5A] font-semibold">Módulo</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl text-[#1A0810]">Procesos</h1>
          <p className="mt-2 text-sm text-[#4a3838] max-w-2xl">
            Vista global de todos los expedientes, trámites y consultas del bufete.
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={() => setProcDialog({ open: true, editing: null })}
            data-testid="procesos-add-button"
            className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]"
          >
            <Plus className="mr-2 h-4 w-4" strokeWidth={2} /> Nuevo proceso
          </Button>
        )}
      </div>

      {/* KPIs — solo operativos (los financieros viven en Cobros) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
        <KpiCard label="Total procesos" value={stats.total} icon={Briefcase} tone="total" />
        <KpiCard label="Activos / en progreso" value={stats.en_progreso} icon={Clock} tone="pendiente" />
      </div>

      {/* Filters */}
      <div className="rounded-xl bg-white border border-[#E5DFD3] p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.8} />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B5D57] font-semibold">Filtros y búsqueda</span>
          {anyFilterActive && (
            <button
              onClick={clearFilters}
              data-testid="procesos-clear-filters"
              className="ml-auto inline-flex items-center gap-1 text-[11px] text-[#C25A5A] hover:text-[#7A1E2E]"
            >
              <X className="h-3 w-3" strokeWidth={1.8} /> Limpiar
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D57]" strokeWidth={1.8} />
            <Input
              data-testid="procesos-search"
              placeholder="Buscar por cliente, título, expediente, abogado..."
              value={query} onChange={(e) => setQuery(e.target.value)}
              className={`${inputCls} pl-10`}
            />
          </div>
          <Select value={fEstado} onValueChange={setFEstado}>
            <SelectTrigger data-testid="filter-estado" className={inputCls}><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Estado: Todos</SelectItem>
              {ESTADOS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fTipo} onValueChange={setFTipo}>
            <SelectTrigger data-testid="filter-tipo" className={inputCls}><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Tipo: Todos</SelectItem>
              {TIPOS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fAbogado} onValueChange={setFAbogado}>
            <SelectTrigger data-testid="filter-abogado" className={inputCls}><SelectValue placeholder="Abogado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Abogado: Todos</SelectItem>
              {abogadosOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fCliente} onValueChange={setFCliente}>
            <SelectTrigger data-testid="filter-cliente" className={`${inputCls} lg:col-span-2`}><SelectValue placeholder="Cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Cliente: Todos</SelectItem>
              {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl bg-white border border-[#E5DFD3] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#7A1E2E] mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <Briefcase className="h-10 w-10 text-[#C19B6C] mx-auto" strokeWidth={1.3} />
            <p className="mt-4 text-sm text-[#6B5D57]">
              {items.length === 0
                ? "Aún no hay procesos registrados."
                : "Sin resultados para los filtros aplicados."}
            </p>
            {items.length === 0 && canEdit && (
              <Button
                onClick={() => setProcDialog({ open: true, editing: null })}
                className="mt-6 bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]"
              >
                <Plus className="mr-2 h-4 w-4" strokeWidth={2} /> Registrar primer proceso
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5EEE0] hover:bg-[#F5EEE0] border-b border-[#E5DFD3]">
                  <TableHead className="text-[#3d2a2a] font-semibold">Cliente</TableHead>
                  <TableHead className="text-[#3d2a2a] font-semibold">Proceso</TableHead>
                  <TableHead className="text-[#3d2a2a] font-semibold">Tipo</TableHead>
                  <TableHead className="text-[#3d2a2a] font-semibold">Estado</TableHead>
                  <TableHead className="text-[#3d2a2a] font-semibold">Fecha</TableHead>
                  <TableHead className="text-[#3d2a2a] font-semibold">Abogado</TableHead>
                  <TableHead className="text-[#3d2a2a] font-semibold text-right">Cobrado / Facturado</TableHead>
                  <TableHead className="text-[#3d2a2a] font-semibold text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => {
                  const estadoCfg = ESTADOS.find((x) => x.value === p.estado);
                  const isFinalized = p.estado === "cerrado";
                  return (
                  <TableRow
                    key={p.id}
                    data-testid={`proceso-row-${p.id}`}
                    className={`border-b border-[#E5DFD3] hover:bg-[#FBF9F4] ${isFinalized ? "bg-[#F6FBF7]" : ""}`}
                    style={{ borderLeft: `3px solid ${estadoCfg?.dot || "#E5DFD3"}` }}
                  >
                    <TableCell>
                      <button
                        onClick={() => navigate(`/clientes/${p.cliente_id}`)}
                        className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                        data-testid={`goto-cliente-${p.cliente_id}`}
                      >
                        <Avatar className="h-9 w-9 ring-1 ring-[#E5DFD3]">
                          <AvatarFallback className={`text-xs font-semibold ${p.cliente_tipo === "juridica" ? "bg-[#C19B6C] text-[#2B0913]" : "bg-[#7A1E2E] text-[#F1EDE4]"}`}>
                            {p.cliente_tipo === "juridica" ? <Building2 className="h-4 w-4" strokeWidth={1.8} /> : (p.cliente_nombre || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="text-left">
                          <div className="text-sm font-medium text-[#1A0810]">{p.cliente_nombre || "—"}</div>
                          <div className="text-xs text-[#6B5D57]">{p.cliente_identificacion || "—"}</div>
                        </div>
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-[#1A0810] max-w-xs">{p.titulo}</div>
                      {p.numero_expediente && (
                        <div className="text-xs text-[#6B5D57] mt-0.5">Exp. {p.numero_expediente}</div>
                      )}
                    </TableCell>
                    <TableCell>{tipoBadge(p.tipo)}</TableCell>
                    <TableCell>{estadoBadge(p.estado)}</TableCell>
                    <TableCell>
                      <span className="text-sm text-[#3d2a2a] whitespace-nowrap">{p.fecha_inicio || "—"}</span>
                      {p.finalized_at && (
                        <div className="text-[10px] text-[#16A34A] font-semibold uppercase tracking-wider mt-0.5">
                          Cerrado {p.fecha_cierre || p.finalized_at?.slice(0,10)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell><span className="text-sm text-[#3d2a2a]">{p.abogado_asignado || "—"}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="font-display text-sm text-[#1F5B2E]">{fmtCRC(p.monto_cobrado)}</div>
                      <div className="text-[11px] text-[#6B5D57]">de {fmtCRC(p.total_facturado)}</div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {/* Finalizar / Reabrir — solo abogado principal */}
                        {isAbogadoPrincipal && !isFinalized && p.estado !== "archivado" && (
                          <button
                            data-testid={`proceso-finalizar-${p.id}`}
                            onClick={() => setFinalizeTarget(p)}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-[#16A34A] hover:bg-[#128036] text-white text-[11px] font-semibold uppercase tracking-wider"
                            title="Finalizar proceso"
                          >
                            <Flag className="h-3.5 w-3.5" strokeWidth={2.2}/> Finalizar
                          </button>
                        )}
                        {isFinalized && (
                          <span
                            data-testid={`proceso-badge-finalizado-${p.id}`}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md bg-[#D8EFDD] text-[#155724] border border-[#7BC48A] text-[11px] font-semibold uppercase tracking-wider"
                            title={p.finalized_by ? `Finalizado por ${p.finalized_by}` : "Finalizado"}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.2}/> Finalizado
                          </span>
                        )}
                        {isAbogadoPrincipal && isFinalized && (
                          <button
                            data-testid={`proceso-reabrir-${p.id}`}
                            onClick={() => reabrir(p)}
                            className="h-8 w-8 rounded-md hover:bg-[#F5EEE0] flex items-center justify-center text-[#6B5D57] hover:text-[#7A1E2E]"
                            title="Reabrir proceso"
                          >
                            <RotateCcw className="h-4 w-4" strokeWidth={1.8}/>
                          </button>
                        )}
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => navigate(`/clientes/${p.cliente_id}?tab=archivo`)}
                          data-testid={`proceso-view-${p.id}`}
                          className="h-8 w-8 hover:bg-[#F5EEE0]"
                          title="Ver en cliente"
                        >
                          <Eye className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.8} />
                        </Button>
                        {canEdit && (
                          <>
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => setCobroDialog({ open: true, proceso: p })}
                              data-testid={`proceso-cobro-${p.id}`}
                              className="h-8 w-8 hover:bg-[#F5EEE0]"
                              title="Registrar cobro"
                            >
                              <Wallet className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.8} />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => setProcDialog({ open: true, editing: p })}
                              data-testid={`proceso-edit-${p.id}`}
                              className="h-8 w-8 hover:bg-[#F5EEE0]"
                              title="Editar"
                            >
                              <UserCog className="h-4 w-4 text-[#3d2a2a]" strokeWidth={1.8} />
                            </Button>
                          </>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => setDeleteTarget(p)}
                            data-testid={`proceso-delete-${p.id}`}
                            className="h-8 w-8 hover:bg-[#FBEDEF]"
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.8} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <ProcesoDialog
        open={procDialog.open}
        editing={procDialog.editing}
        clientes={clientes}
        abogados={abogados}
        onOpenChange={(o) => setProcDialog((s) => ({ ...s, open: o }))}
        onSaved={() => { setProcDialog({ open: false, editing: null }); load(); }}
      />

      <CobroDialog
        open={cobroDialog.open}
        proceso={cobroDialog.proceso}
        onOpenChange={(o) => setCobroDialog((s) => ({ ...s, open: o }))}
        onSaved={() => { setCobroDialog({ open: false, proceso: null }); load(); }}
      />

      {/* Confirmar finalizar */}
      <AlertDialog open={!!finalizeTarget} onOpenChange={(o) => !o && setFinalizeTarget(null)}>
        <AlertDialogContent className="bg-[#FBF9F4] border-[#E5DFD3]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A0810] flex items-center gap-2">
              <Flag className="h-5 w-5 text-[#16A34A]" strokeWidth={2}/>
              Finalizar proceso
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a3838]">
              Vas a marcar como <b>Finalizado</b> el proceso <b>{finalizeTarget?.titulo}</b>.
              <div className="mt-2 rounded-md bg-[#D8EFDD] border border-[#7BC48A] p-3 text-[13px] text-[#155724]">
                Quedará registrado que tú (<b>{user?.name || user?.email}</b>) lo cerraste el día de hoy. Podrás reabrirlo después si es necesario.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={finalizar}
              data-testid="proceso-confirm-finalizar"
              className="bg-[#16A34A] hover:bg-[#128036] text-white"
            >
              <Flag className="h-4 w-4 mr-2" strokeWidth={2.2}/> Sí, finalizar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#FBF9F4] border-[#E5DFD3]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A0810]">Eliminar proceso</AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a3838]">
              ¿Está seguro de eliminar <b>{deleteTarget?.titulo}</b>? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="proceso-confirm-delete" className="bg-[#7A1E2E] hover:bg-[#4A0E1C] text-[#F1EDE4]">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
function KpiCard({ label, value, icon: Icon, tone }) {
  const tones = {
    total: "text-[#7A1E2E] bg-[#F5EEE0] border-[#C19B6C]/40",
    pagado: "text-[#1F5B2E] bg-[#EEF6EE] border-[#1F5B2E]/20",
    pendiente: "text-[#C25A5A] bg-[#FBEDEF] border-[#7A1E2E]/25",
  };
  return (
    <div className="rounded-xl bg-white border border-[#E5DFD3] p-5">
      <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${tones[tone]}`}>
        <Icon className="h-3 w-3" strokeWidth={2} />{label}
      </div>
      <div className="mt-3 font-display text-2xl text-[#1A0810]">{value}</div>
    </div>
  );
}

// ============================================================================
function ProcesoDialog({ open, editing, clientes, abogados, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY_PROC);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) setForm({ ...EMPTY_PROC, ...editing });
      else setForm(EMPTY_PROC);
    }
  }, [open, editing]);

  const save = async () => {
    if (!editing && !form.cliente_id) { toast.error("Debe seleccionar un cliente"); return; }
    if (!form.titulo.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      if (editing) {
        // don't send cliente_id in payload for existing (backend PUT expects ProcesoInput without cliente_id)
        const { cliente_id, ...payload } = form;
        await api.put(`/procesos/${editing.id}`, payload);
        toast.success("Proceso actualizado");
      } else {
        await api.post("/procesos", form);
        toast.success("Proceso creado");
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FBF9F4] border-[#E5DFD3]" data-testid="proceso-global-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A0810]">
            {editing ? "Editar proceso" : "Nuevo proceso"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {!editing && (
            <div>
              <Label className={labelCls}>Cliente *</Label>
              <Select value={form.cliente_id} onValueChange={(v) => setForm({ ...form, cliente_id: v })}>
                <SelectTrigger data-testid="proceso-global-cliente" className={`${inputCls} mt-2`}>
                  <SelectValue placeholder="Seleccionar cliente..." />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nombre} {c.identificacion && `· ${c.identificacion}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {editing && (
            <div className="rounded-md bg-white border border-[#E5DFD3] p-3 text-sm">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Cliente</div>
              <div className="mt-1 text-[#1A0810]">{editing.cliente_nombre || "—"}</div>
            </div>
          )}
          <div>
            <Label className={labelCls}>Título del proceso *</Label>
            <Input data-testid="proceso-global-titulo" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} className={`${inputCls} mt-2`} placeholder="Sucesorio Rojas — Distribución de bienes" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className={labelCls}>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger data-testid="proceso-global-tipo" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelCls}>Estado</Label>
              <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                <SelectTrigger data-testid="proceso-global-estado" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                <SelectContent>{ESTADOS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelCls}>Nº de expediente</Label>
              <Input data-testid="proceso-global-expediente" value={form.numero_expediente} onChange={(e) => setForm({ ...form, numero_expediente: e.target.value })} className={`${inputCls} mt-2`} placeholder="26-000234-CI" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Fecha de inicio</Label>
              <Input data-testid="proceso-global-fecha-inicio" type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} className={`${inputCls} mt-2`} />
            </div>
            <div>
              <Label className={labelCls}>Fecha de cierre</Label>
              <Input data-testid="proceso-global-fecha-cierre" type="date" value={form.fecha_cierre} onChange={(e) => setForm({ ...form, fecha_cierre: e.target.value })} className={`${inputCls} mt-2`} />
            </div>
          </div>
          <div>
            <Label className={labelCls}>Abogado asignado</Label>
            {abogados.length > 0 ? (
              <Select value={form.abogado_asignado || "none"} onValueChange={(v) => setForm({ ...form, abogado_asignado: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="proceso-global-abogado" className={`${inputCls} mt-2`}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Sin asignar —</SelectItem>
                  {abogados.map((a) => <SelectItem key={a.id} value={a.nombre}>{a.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input data-testid="proceso-global-abogado" value={form.abogado_asignado} onChange={(e) => setForm({ ...form, abogado_asignado: e.target.value })} className={`${inputCls} mt-2`} placeholder="Nombre del abogado" />
            )}
          </div>
          <div>
            <Label className={labelCls}>Descripción</Label>
            <Textarea data-testid="proceso-global-descripcion" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className="mt-2 min-h-[80px] bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]" placeholder="Detalles del proceso" />
          </div>
          <div>
            <Label className={labelCls}>Notas internas</Label>
            <Textarea data-testid="proceso-global-notas" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="mt-2 min-h-[64px] bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]" placeholder="Observaciones internas" />
          </div>
        </div>
        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="proceso-global-save" className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Guardar cambios" : "Crear proceso"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
function CobroDialog({ open, proceso, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY_COBRO);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY_COBRO,
        concepto: proceso ? `Honorarios — ${proceso.titulo}` : "",
      });
    }
  }, [open, proceso]);

  const save = async () => {
    if (!proceso) return;
    if (!form.concepto.trim()) { toast.error("El concepto es obligatorio"); return; }
    const monto = parseFloat(form.monto);
    if (isNaN(monto) || monto < 0) { toast.error("Monto inválido"); return; }
    setSaving(true);
    try {
      await api.post(`/clientes/${proceso.cliente_id}/cobros`, {
        ...form,
        monto,
        proceso_id: proceso.id,
      });
      toast.success("Cobro registrado");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  if (!proceso) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FBF9F4] border-[#E5DFD3]" data-testid="cobro-global-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A0810]">Registrar cobro</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="rounded-md bg-white border border-[#E5DFD3] p-3 text-sm">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold">Proceso</div>
            <div className="mt-1 text-[#1A0810] font-medium">{proceso.titulo}</div>
            <div className="mt-0.5 text-xs text-[#6B5D57]">Cliente: {proceso.cliente_nombre}</div>
          </div>
          <div>
            <Label className={labelCls}>Concepto *</Label>
            <Input data-testid="cobro-global-concepto" value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} className={`${inputCls} mt-2`} placeholder="Honorarios..." />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className={labelCls}>Monto *</Label>
              <Input data-testid="cobro-global-monto" type="number" min="0" step="0.01" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} className={`${inputCls} mt-2`} placeholder="850000" />
            </div>
            <div>
              <Label className={labelCls}>Moneda</Label>
              <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                <SelectTrigger data-testid="cobro-global-moneda" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                <SelectContent>{MONEDAS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Fecha</Label>
              <Input data-testid="cobro-global-fecha" type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={`${inputCls} mt-2`} />
            </div>
            <div>
              <Label className={labelCls}>Estado</Label>
              <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                <SelectTrigger data-testid="cobro-global-estado" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                <SelectContent>{COBRO_ESTADOS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelCls}>Método de pago</Label>
              <Select value={form.metodo_pago || "none"} onValueChange={(v) => setForm({ ...form, metodo_pago: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="cobro-global-metodo" className={`${inputCls} mt-2`}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Ninguno —</SelectItem>
                  {METODOS_PAGO.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelCls}>Nº de factura</Label>
              <Input data-testid="cobro-global-factura" value={form.factura} onChange={(e) => setForm({ ...form, factura: e.target.value })} className={`${inputCls} mt-2`} placeholder="2026-014" />
            </div>
          </div>
          <div>
            <Label className={labelCls}>Notas</Label>
            <Textarea data-testid="cobro-global-notas" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="mt-2 min-h-[64px] bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]" placeholder="Observaciones" />
          </div>
        </div>
        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="cobro-global-save" className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar cobro"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
